import { canSaveToLibrary } from '../../../utils/permissions';
import { Form } from 'antd';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as THREE from 'three';
import ToolbarDesign from '../../../pages/DesignerPage/components/ToolbarDesign/ToolbarDesign';
import { DesignerPageContext } from '../../../pages/DesignerPage/DesignerPage';
import { InsideLoader } from '../../../three/loaders/InsideLoader';
import callApi from '../../../utils/api/callAPI';
import {
  InsideType,
  LimitDoor,
  SnapDoor,
  TypeConnerModule,
} from '../../../constants/enum';
import BottomSheet from '../../common/BottomSheet/BottomSheet';
import ButtonIconGhostLarge from '../../common/ButtonIconGhost/ButtonIconGhostLarge';
import ButtonPrimaryLarge from '../../common/ButtonPrimary/ButtonPrimaryLarge';
import { Icon } from '../../icon/icon';
import LeftAccessoryCustomDesignMenu from '../LeftAccessoryCustomDesignMenu/LeftAccessoryCustomDesignMenu';
import LeftCabinetCustomDesignMenu from '../LeftCabinetCustomDesignMenu/LeftCabinetCustomDesignMenu';
import LeftCabinetDesignSidebar from '../LeftCabinetDesignSidebar/LeftCabinetDesignSidebar';
import LeftColumnCabinetCustomDesignMenu from '../LeftColumnCabinetCustomDesignMenu/LeftColumnCabinetCustomDesignMenu';
import LeftDoorCustomDesignMenu from '../LeftDoorCustomDesignMenu/LeftDoorCustomDesignMenu';
import LeftInsideDesignMenu from '../LeftInsideDesignMenu/LeftInsideDesignMenu';
import RightDoorCustomDesignMenu from '../RightDoorCustomDesignMenu/RightDoorCustomDesignMenu';
import RightInsideCustomDesignMenu from '../RightInsideCustomDesignMenu/RightInsideCustomDesignMenu';
import './CabinetEditor.scss';
import SaveModuleLibraryModal from './SaveModuleLibraryModal/SaveModuleLibraryModal';
import ButtonSecondaryLarge from '../../common/ButtonSecondary/ButtonSecondaryLarge';
import { useDispatch, useSelector } from 'react-redux';
import LoadingOverlay from '../../common/LoadingOverlay/LoadingOverlay';
import { openMessageError } from '../../common/Message/openMessageError';
import { openMessageWarning } from '../../common/Message/openMessageWarning';
import { openMessageSuccess } from '../../common/Message/openMessageSuccess';
import openModalFitHeight from '../../common/Modal/openModalConfirmFitHeight';
import { EdgeDetector } from '../../../three/core/layout/EdgeDetector';
import SceneCabinetEditor from '../../../three/scenes/SceneCabinetEditor';
import { roundTo } from '../../../utils/geometry/epsilon';
import { useTranslation } from 'react-i18next';
import { parseJson } from '../../../utils/string/parseJson';
import LeftLightingCustomDesignMenu from '../LeftLightingCustomDesignMenu/LeftLightingCustomDesignMenu';
import { ShortcutUtils } from '../../../utils/shortcuts/ShortcutUtils';
import {
  fetchDoorHandles,
  fetchGroupDoor,
} from '../../../store/actions/door.handle.action';
import { GeometryUtils } from '../../../utils/geometry/GeometryUtils';

export const CabinetEditorContext = createContext();

const CabinetEditor = ({ open, onClose, selectedModel }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const sceneRef = useRef(null);
  const [formLeftCabinetCustom] = Form.useForm();
  const [clonedModule, setClonedModule] = useState(null);
  const { sceneManager, cabinetType, isHideDoor, setIsHideDoor, liftSupport } =
    useContext(DesignerPageContext);
  const [sidebarKey, setSidebarKey] = useState();

  // ==== Drag & pick refs ====
  const isDraggingRef = useRef(false); // kéo preview từ palette
  const draggedModuleRef = useRef(null);
  const previewModelRef = useRef(null);
  const insideLoaderRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mousePositionRef = useRef(new THREE.Vector2());

  const meshClickRef = useRef(null); // mesh/nhóm đang chọn
  const doorCornersRef = useRef([]); // size mesh/nhóm đang chọn
  const childClickRef = useRef(null); // holder (type 10) nếu có

  const isDragInsideRef = useRef(false); // kéo inside
  const isDragInside2Ref = useRef(false); // chặn double-trigger
  const isDragDoorRef = useRef(false); // kéo arrayPointDoor trên cửa
  const isDragHolderRef = useRef(false); // kéo tay nắm/holder

  const [loading, setLoading] = useState(false);

  const prePosRef = useRef(null); // vị trí cũ khi kéo inside
  const pointSnapRef = useRef(null); // snap point khi scale cửa

  const centerPointRef = useRef(null);
  const snapLineRef = useRef(null);
  const SNAP_THRESHOLD = 0.01;

  // Thêm vào phần khai báo states (sau dòng ~28)
  const [moveButtonPos, setMoveButtonPos] = useState(null);
  const [showMoveButton, setShowMoveButton] = useState(false);
  const [productType, setProductType] = useState(null);
  const [groupDoorId, setGroupDoorId] = useState(null);

  const [tick, setTick] = useState(0); // trigger render nhẹ

  const [insideType, setInsideType] = useState();

  const [, setMdTypeClicked] = useState(null);

  const [showSaveLibraryModal, setShowSaveLibraryModal] = useState(false);
  const [beveledDoor, setBeveledDoor] = useState(false);

  const [mdCustomData, setMdCustomData] = useState({
    mdDimensions: {},
    mdOptions: {},
  });
  const [listApplyToAll, setListApplyToAll] = useState([]);
  const [isDimensioning, setIsDimensioning] = useState(false);
  const { groupDoor } = useSelector(state => state.doorAndHandle || {});

  // ========= Helpers =========
  const updateMouseFromEvent = event => {
    const canvas = document.getElementById('canvas-cabinet');
    if (!canvas || !sceneRef.current) return;
    const rect = canvas.getBoundingClientRect();
    mousePositionRef.current.x =
      ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mousePositionRef.current.y =
      -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycasterRef.current.setFromCamera(
      mousePositionRef.current,
      sceneRef.current.camera
    );
  };

  const distanceYZ = (p1, p2) => {
    const dy = p1.y - p2.y,
      dz = p1.z - p2.z;
    return Math.sqrt(dy * dy + dz * dz);
  };
  const distanceYX = (p1, p2) => {
    const dy = p1.y - p2.y,
      dx = p1.x - p2.x;
    return Math.sqrt(dy * dy + dx * dx);
  };
  const round3 = num => {
    return Math.round(num * 1000) / 1000;
  };

  const findClosestPoint = (door, inputPoint, listPoints, min = 0.2) => {
    let best = null,
      dmin = min;
    const orientation = GeometryUtils.getModuleOrientation(
      meshClickRef.current
    );

    listPoints?.forEach(pt => {
      let d;
      if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
        d = distanceYZ(inputPoint, pt.position);
      } else if (
        orientation === 'ROTATED_90' ||
        orientation === 'ROTATED_270'
      ) {
        d = distanceYX(inputPoint, pt.position);
      }

      if (d < dmin) {
        dmin = d;
        best = pt;
      }
    });

    if (best && !door.userData?.drawerUuid) {
      const bestPos = best.getWorldPosition(new THREE.Vector3());
      const doorPos = door.getWorldPosition(new THREE.Vector3());

      const doorBox = new THREE.Box3().setFromObject(door);
      const doorSize = new THREE.Vector3();
      doorBox.getSize(doorSize);

      const doorSizeReal = Math.max(doorSize.x, doorSize.z);
      const limitWidth = door.userData?.limit?.width?.max || LimitDoor.WIDTH;

      let d1, d2;

      if (orientation === 'NORMAL') {
        //Cùng X nên tính theo Z
        d1 = round3(Math.abs(bestPos.z - doorPos.z));
        d2 = round3(Math.abs(bestPos.z - (doorPos.z - doorSizeReal)));
      } else if (orientation === 'ROTATED_90') {
        //Cùng Z tính theo X
        d1 = round3(Math.abs(bestPos.x - doorPos.x));
        d2 = round3(Math.abs(bestPos.x - (doorPos.x - doorSizeReal)));
      } else if (orientation === 'ROTATED_180') {
        //Cùng X nên tính theo Z
        d1 = round3(Math.abs(bestPos.z - doorPos.z));
        d2 = round3(Math.abs(bestPos.z - (doorPos.z + doorSizeReal)));
      } else if (orientation === 'ROTATED_270') {
        //Cùng Z tính theo X
        d1 = round3(Math.abs(bestPos.x - doorPos.x));
        d2 = round3(Math.abs(bestPos.x - (doorPos.x + doorSizeReal)));
      }

      if (d1 <= limitWidth && d2 <= limitWidth) {
        return best;
      }
    }
    if (door.userData?.drawerUuid) {
      return best;
    }
  };

  // ========= Init / Dispose =========
  useEffect(() => {
    if (open) {
      // Tạo SceneCabinetEditor
      const scene = new SceneCabinetEditor('canvas-cabinet', () => {
        // callback chọn mesh trong scene nếu cần
        // console.log("Selected mesh in simple scene:", meshInfo);
      });

      // initialize scene
      scene.initialize({ type: 'wall' });
      sceneRef.current = scene;

      // chuẩn bị InsideLoader
      insideLoaderRef.current = new InsideLoader(scene);

      // Đồng bộ module đang được chọn từ sceneManager
      if (selectedModel && sceneManager && sceneManager.cabinetManager) {
        try {
          const originalModule =
            sceneManager.cabinetManager.getModuleByGltfUuid(selectedModel);
          if (originalModule) {
            setMdCustomData(prevData => ({
              ...prevData,
              mdDimensions: {
                ...prevData.mdDimensions,
                ...originalModule?.formularController?.currentDimensions,
              },
              mdOptions: {
                ...prevData.mdOptions,
                ...originalModule?.formularController?.currentOptions,
              },
              mdLimitedDimensions: {
                ...prevData.mdLimitedDimensions,
                ...originalModule?.moduleData?.limit,
              },
              type: originalModule?.moduleData?.moduleType,
            }));

            // Clone module để edit
            const cloned = cloneModuleForEditing(originalModule);
            setClonedModule(cloned);
            loadModuleAndResetEdges(cloned);

            //Set lại beveled tương ứng vơi module đang chọn
            let beveled = false;
            if (cloned.listInside?.length > 0) {
              cloned.listInside.forEach(inside => {
                if (inside.type === InsideType.DOOR) {
                  beveled = inside.beveled;

                  return;
                }
              });
            }
            insideLoaderRef.current.changeBeveledInside(beveled);
            insideLoaderRef.current.setEditting(true);

            setBeveledDoor(beveled);
          } else {
            setClonedModule(null);
          }
        } catch (error) {
          console.error('Error cloning module:', error);
          setClonedModule(null);
        }
      } else {
        setClonedModule(null);
      }

      return () => {
        if (snapLineRef.current && sceneRef.current) {
          sceneRef.current.scene.remove(snapLineRef.current);
          snapLineRef.current.geometry?.dispose();
          snapLineRef.current.material?.dispose();
          snapLineRef.current = null;
        }
        hideSnapLine();
        try {
          scene.dispose();
        } catch {
          // do nothing
        }
        insideLoaderRef.current = null;
      };
    } else {
      if (sceneRef.current !== null) {
        try {
          sceneRef.current.clearCurrentModule();
        } catch {
          // do nothing
        }
        try {
          sceneRef.current.dispose();
        } catch {
          // do nothing
        }
        sceneRef.current = null;
      }
    }
    setListApplyToAll([]);
  }, [open, selectedModel, sceneManager]);

  useEffect(() => {
    return () => {};
  }, [open]);

  useEffect(() => {
    if (groupDoor.isNeedReloadData) {
      dispatch(fetchGroupDoor());
    }
  }, [groupDoor.isNeedReloadData]);

  useEffect(() => {
    if (groupDoor.data?.length) {
      setGroupDoorId(groupDoor.data[0].value);
    }
  }, [groupDoor.data]);
  useEffect(() => {
    if (!productType || !groupDoorId) return;
    dispatch(
      fetchDoorHandles({
        product_type: productType,
        door_type: 'HINGED',
        orientation: groupDoorId,
      })
    );
  }, [groupDoorId, productType]);
  const setMdCustomDataValue = () => {
    /* eslint-disable no-shadow */
    const clonedModule =
      sceneManager.cabinetManager.currentCabinet.getCloneModule();

    setMdCustomData(prevData => ({
      ...prevData,
      mdDimensions: {
        ...prevData.mdDimensions,
        ...clonedModule?.formularController?.currentDimensions,
      },
      mdOptions: {
        ...prevData.mdOptions,
        ...clonedModule?.formularController?.currentOptions,
      },
      mdLimitedDimensions: {
        ...prevData.mdLimitedDimensions,
        ...clonedModule?.moduleData?.limit,
      },
      type: clonedModule?.moduleData?.moduleType,
    }));
  };

  const loadModuleAndResetEdges = async srcModule => {
    if (!srcModule || !sceneRef.current) return;

    try {
      // LẤY MODULE SAU KHI LOAD
      insideLoaderRef.current.referenceManager.convertAllUuidsToIdentifiers(
        srcModule
      );
      const loadedModule = await sceneRef.current.loadClonedModule(srcModule);
      insideLoaderRef.current.referenceManager.convertAllIdentifiersToUuids(
        loadedModule
      );
      sceneManager.cabinetManager.currentCabinet.setCloneModule(loadedModule);

      setProductType(loadedModule.moduleData.product_type_id || null);

      const currentCabinet = sceneManager?.cabinetManager?.getCurrentCabinet();
      if (
        insideLoaderRef.current &&
        currentCabinet &&
        loadedModule?.listInside?.length > 0
      ) {
        if (typeof currentCabinet.findPoint === 'function')
          currentCabinet.findPoint();
        if (typeof sceneRef.current.drawEdgeLine === 'function')
          sceneRef.current.drawEdgeLine();
      } else {
        console.log('No inside items to reset edge dependencies for');
      }
    } catch (err) {
      console.error('Error loading cloned module or resetting edges:', err);
    }
  };

  const loadRealModelWithInsideLoader = async (
    moduleData,
    intersection,
    module,
    currentCabinet,
    typeHau
  ) => {
    const skipCheckTypes = [
      InsideType.DOOR,
      InsideType.BRACE,
      InsideType.HANDLE,
    ];
    const type = moduleData.type;

    // if (!skipCheckTypes.includes(type)) {
    //   const isAllowed = module.exclude_types?.includes(type);
    //   if (!isAllowed) {
    //     openMessageError({
    //       content: t('Failed to load module {{moduleId}}', {
    //         moduleId: moduleData?.id,
    //       }),
    //     });
    //     return;
    //   }
    // }
    if (insideLoaderRef.current) {
      await insideLoaderRef.current.loadRealModel(
        moduleData,
        intersection,
        module,
        currentCabinet,
        null,
        typeHau,
        liftSupport?.data
      );

      if (
        moduleData.type === InsideType.VPANEL ||
        moduleData.type === InsideType.VPANEL2
      ) {
        await insideLoaderRef.current.adjustBackPanelForHPanel(
          module,
          sceneManager
        );
      }

      setMdCustomDataValue();

      setTick(value => value + 1);
      setTimeout(async () => {
        await sceneManager.textureController.reloadTexture({ module });
      }, 100);
      // await sceneManager.calculateTotalCost();
    } else {
      console.error('InsideLoader instance not available');
    }
  };

  /**
   * Clone module để edit mà không ảnh hưởng module gốc
   */
  const cloneModuleForEditing = originalModule => {
    const ModuleClass = originalModule.constructor;
    const clonedModuleData = parseJson(
      JSON.stringify(originalModule.moduleData)
    );

    const clonedModule = new ModuleClass(
      clonedModuleData,
      originalModule.moduleType || originalModule.cabinetType,
      sceneManager,
      originalModule.cabinet
    );

    clonedModule.id = `${originalModule.id}_editor_${Date.now()}`;
    clonedModule.actualSize =
      originalModule.actualSize.clone?.() || originalModule.actualSize;
    clonedModule.boundingBox =
      originalModule.boundingBox.clone?.() || originalModule.boundingBox;

    if (originalModule.customData) {
      clonedModule.customData = { ...originalModule.customData };
    }

    clonedModule.isEditorClone = true;
    clonedModule.group = originalModule.group;
    clonedModule.originalModuleId = originalModule.id;
    clonedModule.gltfUuid =
      originalModule.gltfUuid || originalModule.group?.userData?.gltfUuid;
    clonedModule.textures = originalModule.textures;
    clonedModule.beveled = originalModule.beveled;
    clonedModule.formularController = originalModule.formularController.clone();

    if (originalModule.doorType)
      clonedModule.doorType = originalModule.doorType;
    if (originalModule.handleType)
      clonedModule.handleType = originalModule.handleType;
    if (originalModule.material)
      clonedModule.material = originalModule.material;
    if (originalModule.color) clonedModule.color = originalModule.color;
    if (originalModule.listInside && originalModule.listInside.length > 0) {
      clonedModule.listInside = originalModule.listInside.map(item => {
        // Clone object
        const clonedItem = {
          _id: item._id,
          glb: item.glb,
          id: item.id,
          name: item.name,
          price: item.price,
          beveled: item.beveled,
          dependent: item.dependent,
          doorDistance: item.doorDistance,
          gltfUuid: item.gltfUuid,
          limit: item.limit,
          type: item.type,
        };

        if (item.gltf) {
          clonedItem.gltf = item.gltf; // Tạm thời giữ reference cũ
        }

        // Copy các thuộc tính khác nếu có
        Object.keys(item).forEach(key => {
          if (!Object.hasOwn(clonedItem, key)) {
            if (
              item[key] &&
              typeof item[key] === 'object' &&
              typeof item[key].clone === 'function'
            ) {
              clonedItem[key] = item[key].clone();
            } else if (item[key] && typeof item[key] === 'object') {
              clonedItem[key] = parseJson(JSON.stringify(item[key]));
            } else {
              clonedItem[key] = item[key];
            }
          }
        });

        return clonedItem;
      });
    }

    return clonedModule;
  };

  // (Optional) clear insides util – giữ lại nếu cần
  function clearModuleInsides(module) {
    if (!module || !module.group) return;

    const toRemove = [];

    // Traverse toàn bộ node trong group
    module.group.traverse(child => {
      if (child.userData?.isInside) {
        toRemove.push(child);
      }
    });

    // Xóa node ra khỏi scene
    toRemove.forEach(node => {
      if (node.parent) {
        node.parent.remove(node);
      }
    });

    // Reset lại listInside cho đồng bộ
    module.listInside = [];
  }

  async function applyDesignToModule({
    payload,
    originalModule,
    currentCabinet,
    insideLoader,
  }) {
    await insideLoader.loadInsideDesignOfModule(
      payload,
      originalModule,
      currentCabinet
    );
  }

  const handleOpenSaveLibraryModal = () => {
    if (!clonedModule) {
      openMessageWarning({ content: t('No module to save!') });
      return;
    }
    setShowSaveLibraryModal(true);
  };

  const handleSaveToLibrary = async formDataSave => {
    try {
      setLoading(true);
      if (!clonedModule || !sceneRef.current) {
        openMessageWarning({ content: t('No module to save!') });
        return;
      }

      // 2) Lưu thông tin inside theo logic saveDesign
      const insidePayload =
        insideLoaderRef.current?.saveInsideDesignOfModule(clonedModule) || [];

      // 3) Thu thập metadata của module
      const libraryData = {
        // Kích thước và options
        dimensions: clonedModule.formularController?.currentDimensions || null,
        options: clonedModule.formularController?.currentOptions || null,

        // Vật liệu và màu sắc
        // material: clonedModule.material || null,
        // color: clonedModule.color || null,
        // doorType: clonedModule.doorType || null,
        // handleType: clonedModule.handleType || null,
        beveled: clonedModule.beveled,

        // Inside design
        inside: insidePayload,
      };

      const formData = new FormData();
      formData.append('name', formDataSave?.name.trim());
      formData.append('parent_id', clonedModule.moduleData.id || '');
      formData.append(
        'category_id ',
        clonedModule.moduleData.category_id || ''
      );
      formData.append('library_data', JSON.stringify(libraryData));
      formData.append('thumbnail', formDataSave.thumbnailFile || null);

      const res = await callApi({
        endpoint: 'modules/v3/basemodules/library',
        method: 'post',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (res?.data?.success === true) {
        openMessageSuccess({
          content: t('Saved module {{name}} to the library!', {
            name: formDataSave?.name.trim(),
          }),
        });
      }
    } catch (error) {
      setLoading(false);
      console.error('Lỗi khi lưu module vào thư viện:', error);
      openMessageError({ content: t('Failed to save module to library!') });
      throw error; // Throw để modal không đóng khi có lỗi
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      await formLeftCabinetCustom.validateFields();

      const originalModule =
        sceneManager.cabinetManager.getModuleByGltfUuid(selectedModel);

      //Xác định lưu thay đổi
      clonedModule.isSaveClone = true;
      originalModule.isSaveClone = true;

      const modules = Array.from(
        sceneManager.cabinetManager.currentCabinet.modules
      );
      const modulesUpper = modules.filter(([key, module]) =>
        module.moduleType.includes('upper')
      );
      const modulesCeiling = modules.filter(([key, module]) =>
        module.moduleType.includes('ceiling')
      );

      if (
        originalModule?.formularController?.currentDimensions?.height !==
          mdCustomData.mdDimensions.height &&
        (modulesUpper.length > 0 || modulesCeiling.length > 0)
      ) {
        setTimeout(() => {
          openModalFitHeight({
            func: () => {
              sceneManager.autoFitModuleController.moveUpperFollowHeightestLowerCabinet(
                { insideLoaderRef }
              );

              sceneManager.autoFitModuleController.moveCeilingFollowHeightestUpperCabinet(
                { insideLoaderRef }
              );
            },
          });
        });
      }

      /// Comment lại để trái scale tùy ý, nên check gia trị như bên dưới
      /// Không có đoạn này thì khi trong editer thay đổi kích thước khác với module gốc thì scale sai
      await sceneManager.autoFitModuleController.scaleModule(
        insideLoaderRef,
        selectedModel,
        mdCustomData.mdDimensions,
        mdCustomData.mdOptions,
        false
      );

      if (listApplyToAll.length) {
        const mdDimensions = {};
        const mdOptions = {};
        if (listApplyToAll.includes('height')) {
          mdDimensions['height'] = mdCustomData.mdDimensions.height;
        }
        if (listApplyToAll.includes('depth')) {
          mdDimensions['depth'] = mdCustomData.mdDimensions.depth;
        }
        if (listApplyToAll.includes('caoChan')) {
          mdOptions['caoChan'] = mdCustomData.mdOptions.caoChan;
        }
        if (listApplyToAll.includes('DDTHau')) {
          mdOptions['DDTHau'] = mdCustomData.mdOptions.DDTHau;
        }

        sceneManager.autoFitModuleController.applyToAllCabinets({
          selectedModelId: selectedModel,
          allowFit: undefined,
          mdDimensions,
          mdOptions,
        });
      }

      const currentCabinet = sceneManager.cabinetManager.getCurrentCabinet();

      //Set door dependent
      insideLoaderRef.current?.setDependentDoorPosition(
        clonedModule,
        currentCabinet
      );

      const payload = insideLoaderRef.current?.saveDesign(clonedModule);
      clearModuleInsides(originalModule);

      //update lại beveled theo chỉnh sửa
      originalModule.beveled = payload.cabinet.beveled;

      // CHỜ xong apply
      await applyDesignToModule({
        payload,
        originalModule,
        currentCabinet,
        insideLoader: insideLoaderRef.current,
      });

      await insideLoaderRef.current.adjustBackPanelForHPanel(
        originalModule,
        sceneManager
      );
      await sceneManager.textureController.reloadTexture({
        module: originalModule,
      });
      // await sceneManager.calculateTotalCost?.({});

      // ĐẾN ĐÂY đảm bảo listInside đã được push xong
      // console.log('listInside after apply:', originalModule.listInside);

      //Cập nhật lại MB,VB
      sceneManager.updateAllCountertopAndBacksplash();

      setTimeout(async () => {
        await sceneManager.calculateTotalCost?.({});
      }, 500);

      onClose();
    } catch {
      openMessageError({ content: t('Failed to save changes!') });
    }
  };

  // ===== CONTEXT VALUE =====
  const contextValue = {
    sceneManager,
    clonedModule,
    setClonedModule,
    mdCustomData,
    setMdCustomData,
    listApplyToAll,
    setListApplyToAll,
    insideLoaderRef,
    sceneRef,
    insideType,
    setTick,
    meshClickRef,
  };

  // gợi ý chia đôi
  // Tìm 2 cạnh gần nhất theo trục Z (cho type 1 - dọc)
  const findClosestZEdges = (
    intersectionPoint,
    currentCabinet,
    clonedModule
  ) => {
    if (!intersectionPoint || !currentCabinet || !clonedModule) return null;

    const meshPoints = currentCabinet.getActiveMeshPoints(clonedModule);
    if (!meshPoints || meshPoints.length === 0) return null;

    const orientation = insideLoaderRef.current?.getModuleOrientation(
      clonedModule.group
    );

    if (orientation === 'NORMAL') {
      const result = EdgeDetector.findClosestZEdges(
        undefined,
        meshPoints,
        intersectionPoint,
        [InsideType.COL_PANEL]
      );
      return result;
    } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
      const result = EdgeDetector.findClosestZEdgesR270(
        undefined,
        meshPoints,
        intersectionPoint,
        [InsideType.COL_PANEL]
      );
      return result;
    }

    return null;
  };

  // Tìm 2 cạnh gần nhất theo trục Y (cho type 0 - ngang)
  const findClosestYEdges = (
    intersectionPoint,
    currentCabinet,
    clonedModule
  ) => {
    if (!intersectionPoint || !currentCabinet || !clonedModule) return null;

    const meshPoints = currentCabinet.getActiveMeshPoints(clonedModule);
    if (!meshPoints || meshPoints.length === 0) return null;

    const orientation = insideLoaderRef.current?.getModuleOrientation(
      clonedModule.group
    );

    if (orientation === 'NORMAL') {
      const result = EdgeDetector.findClosestYEdges(
        undefined,
        meshPoints,
        intersectionPoint,
        null,
        undefined,
        true
      );
      return result;
    } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
      const result = EdgeDetector.findClosestYEdgesR270(
        undefined,
        meshPoints,
        intersectionPoint,
        null,
        undefined,
        true
      );
      return result;
    }

    return null;
  };

  // Tính trung điểm cho type 1 (dọc - theo Z)
  const calculateCenterPointVertical = (
    leftEdge,
    rightEdge,
    clonedModule,
    intersectionPoint
  ) => {
    if (!leftEdge || !rightEdge || !clonedModule) return null;

    const leftWorldPos = leftEdge.mesh.getWorldPosition(new THREE.Vector3());
    const rightWorldPos = rightEdge.mesh.getWorldPosition(new THREE.Vector3());

    const orientation = insideLoaderRef.current?.getModuleOrientation(
      clonedModule.group
    );

    if (orientation === 'NORMAL') {
      const centerZ = (leftWorldPos.z + rightWorldPos.z) / 2;
      return new THREE.Vector3(
        intersectionPoint.x,
        intersectionPoint.y,
        centerZ
      );
    } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
      const centerX = (leftWorldPos.x + rightWorldPos.x) / 2;
      return new THREE.Vector3(
        centerX,
        intersectionPoint.y,
        intersectionPoint.z
      );
    }

    return null;
  };

  // Tính trung điểm cho type 0 (ngang - theo Y)
  const calculateCenterPointHorizontal = (
    bottomEdge,
    topEdge,
    clonedModule,
    intersectionPoint
  ) => {
    if (!bottomEdge || !topEdge || !clonedModule) return null;

    const bottomWorldPos = bottomEdge.mesh.getWorldPosition(
      new THREE.Vector3()
    );
    const topWorldPos = topEdge.mesh.getWorldPosition(new THREE.Vector3());

    const orientation = insideLoaderRef.current?.getModuleOrientation(
      clonedModule.group
    );

    // Tính trung điểm theo Y (chiều cao)
    const centerY = (bottomWorldPos.y + topWorldPos.y) / 2;

    if (orientation === 'NORMAL') {
      return new THREE.Vector3(
        intersectionPoint.x,
        centerY,
        intersectionPoint.z
      );
    } else if (orientation === 'ROTATED_90' || orientation === 'ROTATED_270') {
      return new THREE.Vector3(
        intersectionPoint.x,
        centerY,
        intersectionPoint.z
      );
    }

    return null;
  };

  // Kiểm tra gần trung điểm
  const isNearCenterPoint = (
    point,
    centerPoint,
    previewType,
    threshold = SNAP_THRESHOLD
  ) => {
    if (!point || !centerPoint) return false;

    const clonedModule =
      sceneManager?.cabinetManager?.currentCabinet?.getCloneModule();
    if (!clonedModule) return false;

    const orientation = insideLoaderRef.current?.getModuleOrientation(
      clonedModule.group
    );

    // Type 1: Snap theo chiều dọc (Z)
    if (previewType === 1) {
      if (orientation === 'NORMAL') {
        return Math.abs(point.z - centerPoint.z) < threshold;
      } else if (
        orientation === 'ROTATED_90' ||
        orientation === 'ROTATED_270'
      ) {
        return Math.abs(point.x - centerPoint.x) < threshold;
      }
    }

    // Type 0: Snap theo chiều ngang (Y)
    if (previewType === 0) {
      return Math.abs(point.y - centerPoint.y) < threshold;
    }

    return false;
  };

  // Tạo/cập nhật snap line theo type
  const createOrUpdateSnapLine = (
    centerPoint,
    clonedModule,
    previewType,
    isNear = false
  ) => {
    if (!sceneRef.current || !centerPoint || !clonedModule) {
      hideSnapLine();
      return;
    }

    // Tạo line lần đầu
    if (!snapLineRef.current) {
      const material = new THREE.LineDashedMaterial({
        color: 0x1e90ff,
        dashSize: 0.1,
        gapSize: 0.05,
        linewidth: 2,
        transparent: true,
        opacity: 0.8,
        depthTest: false,
      });

      const geometry = new THREE.BufferGeometry();
      snapLineRef.current = new THREE.Line(geometry, material);
      snapLineRef.current.renderOrder = 1000;
      sceneRef.current.scene.add(snapLineRef.current);
    }

    const moduleBox = new THREE.Box3().setFromObject(clonedModule.group);

    let startPoint, endPoint;

    // Type 1: Line dọc (theo trục Y)
    if (previewType === 1) {
      startPoint = new THREE.Vector3(
        centerPoint.x,
        moduleBox.min.y - 0.05,
        centerPoint.z
      );

      endPoint = new THREE.Vector3(
        centerPoint.x,
        moduleBox.max.y + 0.05,
        centerPoint.z
      );
    }

    // Type 0: Line ngang (theo trục Z)
    else if (previewType === 0) {
      const orientation = insideLoaderRef.current?.getModuleOrientation(
        clonedModule.group
      );

      if (orientation === 'NORMAL') {
        // Line chạy dọc theo Z (ngang khi nhìn)
        startPoint = new THREE.Vector3(
          centerPoint.x,
          centerPoint.y,
          moduleBox.min.z - 0.05
        );

        endPoint = new THREE.Vector3(
          centerPoint.x,
          centerPoint.y,
          moduleBox.max.z + 0.05
        );
      } else if (
        orientation === 'ROTATED_90' ||
        orientation === 'ROTATED_270'
      ) {
        // Line chạy dọc theo X (ngang khi nhìn)
        startPoint = new THREE.Vector3(
          moduleBox.min.x - 0.05,
          centerPoint.y,
          centerPoint.z
        );

        endPoint = new THREE.Vector3(
          moduleBox.max.x + 0.05,
          centerPoint.y,
          centerPoint.z
        );
      }
    }

    // Cập nhật geometry
    const positions = new Float32Array([
      startPoint.x,
      startPoint.y,
      startPoint.z,
      endPoint.x,
      endPoint.y,
      endPoint.z,
    ]);

    snapLineRef.current.geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(positions, 3)
    );

    snapLineRef.current.computeLineDistances();

    // Thay đổi màu khi snap
    if (isNear) {
      snapLineRef.current.material.color.setHex(0x00ff00);
      snapLineRef.current.material.opacity = 1.0;
      snapLineRef.current.material.dashSize = 0.15;
      snapLineRef.current.material.gapSize = 0.08;
    } else {
      snapLineRef.current.material.color.setHex(0x1e90ff);
      snapLineRef.current.material.opacity = 0.8;
      snapLineRef.current.material.dashSize = 0.1;
      snapLineRef.current.material.gapSize = 0.05;
    }

    snapLineRef.current.visible = true;
  };
  // Ẩn snap line
  const hideSnapLine = () => {
    if (snapLineRef.current) {
      snapLineRef.current.visible = false;
    }
    centerPointRef.current = null;
  };
  // gợi ý chia đôi

  // ========= Preview drag (palette) =========
  const updatePreviewPosition = () => {
    if (!sceneRef.current || !previewModelRef.current) return;

    raycasterRef.current.setFromCamera(
      mousePositionRef.current,
      sceneRef.current.camera
    );
    const intersects = raycasterRef.current.intersectObjects(
      sceneRef.current.planToCheck.children,
      true
    );
    if (intersects.length > 0) {
      const intersectionPoint = intersects[0].point;
      previewModelRef.current.position.copy(
        getAdjustedPoint(intersectionPoint, previewModelRef.current)
      );
    }
  };

  const handleDragStart = (e, module) => {
    e.preventDefault();
    if (!sceneRef.current) return;
    isDraggingRef.current = true;
    draggedModuleRef.current = module;

    if (meshClickRef.current) {
      meshClickRef.current = null;
      insideLoaderRef.current?.clearLine();
      insideLoaderRef.current?.clearLabel();
      childClickRef.current = null;
      sceneRef.current?.removeBoxes?.();
      sceneRef.current?.removeAllPoints?.();
      sceneRef.current?.removeWireframeBoxes?.();
      setShowMoveButton(false);
    }

    sceneRef.current.loadModulePreview(module).then(preview => {
      if (preview) {
        sceneRef.current.scene.add(preview);
        previewModelRef.current = preview;
        updatePreviewPosition();
      }
    });
  };

  const findParentInside = object => {
    let current = object;
    while (current?.parent && !current?.userData.isInside) {
      current = current.parent;
    }
    return current;
  };

  const findTopLevelParent = object => {
    let current = object;
    while (current?.parent && current?.parent?.type !== 'Scene') {
      current = current.parent;
    }
    return current;
  };

  const handleChooseDoorAccessory = (e, moduleData, applyToAll) => {
    e.preventDefault();
    if (!meshClickRef.current) {
      openMessageError({ content: t('Please select a door to add handle') });
      return;
    }

    const currentCabinet = sceneManager.cabinetManager.getCurrentCabinet();
    const cloneModule =
      sceneManager.cabinetManager.currentCabinet.getCloneModule();

    if (!currentCabinet || !cloneModule) {
      openMessageError({
        content: t(''),
      });
      return;
    }

    cloneModule.listInside.forEach(inside => {
      if (inside.type === InsideType.DOOR) {
        const door = sceneRef.current.findGLTFByUuid(inside.gltf.uuid);

        if (!door) {
          openMessageError({
            content: t(
              'No module containing the door was found to add the handle.'
            ),
          });
          return;
        }

        //Thay đổi tay nắm
        const addHandleToCabinet = handle => {
          const data = {
            _id: handle.uuid,
            glb: moduleData.model,
            type: moduleData.type,
            gltfUuid: handle.uuid,
            gltf: handle,
            thumbnail_url: handle.userData.thumbnail_url,
            price: moduleData.price,
            name: moduleData.name,
          };
          handle.userData.isInside = true;
          handle.userData.model = moduleData.model;
          clonedModule.listInside.push(data);
          clonedModule.group.add(handle);
        };

        insideLoaderRef.current.loadHandleModel(
          moduleData,
          cloneModule,
          door,
          addHandleToCabinet
        );
      }
    });

    if (applyToAll) {
      const lstDoorWithHandles = sceneManager.getAllDoorsWithHandles();

      lstDoorWithHandles.forEach(door => {
        const model = findTopLevelParent(door);
        const foundModule = currentCabinet.getModuleByGroupUuid(model.uuid);

        if (!currentCabinet || !foundModule) {
          openMessageError({
            content: t(
              'No module containing the door was found to add the handle.'
            ),
          });
          return;
        }

        //Thay đổi tay nắm
        const addHandleToCabinet = handle => {
          const data = {
            _id: handle.uuid,
            glb: moduleData.model,
            type: moduleData.type,
            gltfUuid: handle.uuid,
            gltf: handle,
            thumbnail_url: handle.userData.thumbnail_url,
            price: moduleData.price,
            name: moduleData.name,
          };
          handle.userData.isInside = true;
          handle.userData.model = moduleData.model;
          foundModule.listInside.push(data);
          foundModule.group.add(handle);
        };

        insideLoaderRef.current.loadHandleModel(
          moduleData,
          foundModule,
          door,
          addHandleToCabinet
        );
      });
    }
  };

  // Thêm sau các helper functions (sau dòng ~100)
  const project3DToScreen = position3D => {
    if (!sceneRef.current || !position3D) return null;

    const canvas = document.getElementById('canvas-cabinet');
    if (!canvas) return null;

    const vector = new THREE.Vector3();
    vector.copy(position3D);
    vector.project(sceneRef.current.camera);

    const rect = canvas.getBoundingClientRect();
    const x = (vector.x * 0.5 + 0.5) * rect.width + rect.left;
    const y = (-vector.y * 0.5 + 0.5) * rect.height + rect.top;

    return { x, y };
  };

  const updateMoveButtonPosition = targetMesh => {
    if (!targetMesh || !sceneRef.current) {
      setShowMoveButton(false);
      return;
    }

    const box = new THREE.Box3().setFromObject(targetMesh);

    // Tính center thủ công từ min và max
    const centerPos = new THREE.Vector3(
      box.max.x,
      box.max.y,
      (box.min.z + box.max.z) / 2
    );

    const screenPos = project3DToScreen(centerPos);
    if (screenPos) {
      setMoveButtonPos(screenPos);
      setShowMoveButton(true);
    }
  };

  const handleCanvasClick = event => {
    const canvas = document.getElementById('canvas-cabinet');
    if (event.target !== canvas || !sceneRef.current) return;

    updateMouseFromEvent(event);

    const currentCabinet = sceneManager.cabinetManager.getCurrentCabinet();
    const cloneModule =
      sceneManager.cabinetManager.currentCabinet.getCloneModule();
    const intersects = raycasterRef.current.intersectObjects(
      cloneModule?.group?.children,
      true
    );

    if (!intersects?.length) {
      meshClickRef.current = null;
      setMdTypeClicked(null);
      insideLoaderRef.current?.clearLine();
      insideLoaderRef.current?.clearLabel();
      childClickRef.current = null;
      sceneRef.current?.removeBoxes?.();
      sceneRef.current?.removeAllPoints?.();
      sceneRef.current?.removeWireframeBoxes?.();
      setShowMoveButton(false); // ẨN BUTTON
      setTick(value => value + 1);
      return;
    }

    const hit = intersects[0].object;
    let picked = null;

    if (
      hit?.userData?.type === InsideType.HANDLE ||
      meshClickRef.current?.userData?.type === InsideType.ACCESSORY_GENERAL ||
      meshClickRef.current?.userData?.type === InsideType.ACCESSORY_COOKER ||
      meshClickRef.current?.userData?.type ===
        InsideType.ACCESSORY_DISH_LOWER ||
      meshClickRef.current?.userData?.type === InsideType.ACCESSORY_FAUCET ||
      meshClickRef.current?.userData?.type === InsideType.ACCESSORY_FRIDGE ||
      meshClickRef.current?.userData?.type === InsideType.ACCESSORY_HOOD ||
      meshClickRef.current?.userData?.type ===
        InsideType.ACCESSORY_DISH_UPPER ||
      meshClickRef.current?.userData?.type === InsideType.ACCESSORY_SINK
    ) {
      picked = meshClickRef.current;
      childClickRef.current = hit;
    } else {
      const parentUuid = hit?.userData?.parentGltf;
      if (parentUuid) {
        const parent = cloneModule.group.children.find(
          ch => ch.uuid === parentUuid
        );
        picked = parent || hit;
      } else {
        picked = hit;
      }
      childClickRef.current = null;
    }

    setInsideType(picked?.userData?.type);

    if (picked?.userData?.type !== undefined) {
      setTick(value => value + 1);

      if (!picked?.userData?.isInside) {
        picked = findParentInside(picked);
      }

      meshClickRef.current = picked;
      setMdTypeClicked(picked.userData.type);

      const scene = sceneRef.current;
      if (picked.userData.type !== InsideType.DOOR) {
        if (
          picked.userData.type !== InsideType.LED_EFFECT &&
          picked.userData.type !== InsideType.LED_CORNER &&
          picked.userData.type !== InsideType.LED_RECESSED &&
          picked.userData.type !== InsideType.ACCESSORY_SINK &&
          picked.userData.type !== InsideType.ACCESSORY_HOOD &&
          picked.userData.type !== InsideType.ACCESSORY_FRIDGE &&
          picked.userData.type !== InsideType.ACCESSORY_WASHER &&
          picked.userData.type !== InsideType.ACCESSORY_FAUCET &&
          picked.userData.type !== InsideType.ACCESSORY_COOKER
        ) {
          const boxData = insideLoaderRef?.current?.drawBoxBorder?.(
            picked,
            currentCabinet,
            cloneModule
          );
          scene?.createBoxBorder?.(boxData);
          if (
            ![
              InsideType.DRAWER,
              InsideType.U_DRAWER,
              InsideType.ACCESSORY,
            ].includes(picked.userData.type)
          )
            scene?.createWireframeBox?.(picked);
          if (picked.userData.type === InsideType.DRAWER) {
            scene?.createWireframeBoxGap?.(picked);
          }

          // HIỂN THỊ MOVE BUTTON
          updateMoveButtonPosition(picked);
        }
      } else {
        doorCornersRef.current = scene?.setPointDoor(picked);
        setShowMoveButton(false); // ẨN BUTTON CHO DOOR
      }

      if (!isDragInside2Ref.current) {
        // mở panel setting nếu muốn
      } else {
        isDragInside2Ref.current = false;
      }
    } else {
      doorCornersRef.current = [];

      childClickRef.current = null;
      insideLoaderRef.current?.clearLine();
      insideLoaderRef.current?.clearLabel();
      sceneRef.current?.removeBoxes?.();
      sceneRef.current?.removePoints?.();
      sceneRef.current?.removeWireframeBoxes?.();
      setShowMoveButton(false); // ẨN BUTTON
    }
  };

  const getAdjustedPoint = (intersectionPoint, model) => {
    if (!model) return intersectionPoint.clone();

    const box = new THREE.Box3().setFromObject(model);
    const size = new THREE.Vector3();
    box.getSize(size);

    const adjusted = intersectionPoint.clone();
    adjusted.y -= size.y / 2;
    adjusted.z += size.z / 2;

    return adjusted;
  };

  const handleMouseDown = event => {
    if (!sceneRef.current) return;

    // KIỂM TRA CLICK VÀO MOVE BUTTON (kiểm tra trước)
    if (event.target.closest('.btn-inside-move')) {
      if (sceneRef.current.controls) {
        sceneRef.current.controls.enabled = false;
      }
      isDragInsideRef.current = true;
      isDragInside2Ref.current = true;
      prePosRef.current = meshClickRef.current.position.clone();
      setShowMoveButton(false); // ẨN BUTTON KHI BẮT ĐẦU DRAG
      return;
    }

    updateMouseFromEvent(event);

    // Ưu tiên: holder (type 10)
    if (childClickRef.current?.userData?.type === 10) {
      const hit = raycasterRef.current.intersectObjects([
        childClickRef.current,
      ]);
      if (hit?.length) {
        if (sceneRef.current.controls) {
          sceneRef.current.controls.enabled = false;
        }
        isDragHolderRef.current = true;
        setShowMoveButton(false); // ẨN BUTTON KHI DRAG HOLDER
        return;
      }
    }

    // Kéo handle scale cửa (arrayPointDoor)
    const moves = sceneRef.current.arrayPointDoor || [];
    if (moves.length) {
      const hit = raycasterRef.current.intersectObjects(moves);
      if (hit?.length) {
        const intersected = hit[0].object;
        const point = hit[0].point;

        const scene = sceneRef.current;
        scene.pIntersect?.copy?.(point);
        scene.plane?.setFromNormalAndCoplanarPoint?.(
          scene.normal,
          scene.pIntersect
        );
        scene.shift?.subVectors?.(intersected.position, point);

        isDragDoorRef.current = true;
        scene.dragObject = intersected;
        if (scene.controls) {
          scene.controls.enabled = false;
        }
        setShowMoveButton(false); // ẨN BUTTON KHI DRAG DOOR
        return;
      }
    }
  };

  // ========= MOUSEMOVE: kéo preview / inside / holder / door =========
  const handleMouseMoveExtended = event => {
    if (!sceneRef.current) return;

    // Gợi ý con trỏ
    const moves = sceneRef.current.arrayPointDoor || [];
    let isPointer = false;
    if (moves.length) {
      updateMouseFromEvent(event);
      const hitMove = raycasterRef.current.intersectObjects(moves);
      if (hitMove?.length) isPointer = true;
    }
    if (meshClickRef.current) {
      const hitMesh = raycasterRef.current.intersectObjects([
        meshClickRef.current,
      ]);
      if (hitMesh?.length) isPointer = true;
    }
    document.body.style.cursor = isPointer ? 'pointer' : 'default';

    // 1) Kéo preview (đã có logic trong effect cũ, giữ đơn giản ở đây)
    if (isDraggingRef.current && previewModelRef.current) {
      updateMouseFromEvent(event);
      const hits = raycasterRef.current.intersectObjects(
        sceneRef.current.planToCheck.children,
        true
      );
      if (hits.length) {
        const point = hits[0].point;
        previewModelRef.current.position.copy(
          getAdjustedPoint(point, previewModelRef.current)
        );
      }
    }

    // 2) Kéo holder theo mặt cửa
    if (isDragHolderRef.current) {
      updateMouseFromEvent(event);
      if (!meshClickRef.current) return;
      const hit = raycasterRef.current.intersectObjects([meshClickRef.current]);
      if (hit?.length) {
        const point = hit[0].point;
        childClickRef.current.position.y = point.y;
        childClickRef.current.position.z = point.z;

        const currentCabinet = sceneManager.cabinetManager.getCurrentCabinet();
        currentCabinet?.updateHolderPadding?.(
          meshClickRef.current,
          childClickRef.current
        );

        sceneRef.current?.display?.createWireframeBox?.(childClickRef.current);
        setTick(value => value + 1);
      }
    }

    // Trong handleMouseMoveExtended, phần kéo inside
    if (isDragInsideRef.current) {
      updateMouseFromEvent(event);
      const boxes = sceneRef.current.boxes || [];
      const hit = raycasterRef.current.intersectObjects(boxes);
      if (hit?.length) {
        const point = hit[0].point;
        const currentCabinet = sceneManager.cabinetManager.getCurrentCabinet();
        const cloneModule =
          sceneManager.cabinetManager.currentCabinet.getCloneModule();

        insideLoaderRef?.current?.moveMesh?.(
          meshClickRef.current,
          point,
          prePosRef.current,
          sceneRef.current,
          currentCabinet,
          cloneModule
        );

        sceneRef.current?.removeWireframeBoxes?.();
        sceneRef.current?.createWireframeBox?.(meshClickRef.current);
        sceneRef.current?.handleDrawBorder();

        // XÓA DÒNG NÀY - không update button khi đang drag
        // updateMoveButtonPosition(meshClickRef.current);

        sceneRef.current?.drawEdgeLine?.();
        sceneRef.current?.display?.removePoints?.();
        sceneRef.current?.display?.removeWireframeBoxes?.();

        setTick(value => value + 1);
      }
    }

    // 4) Kéo handle cửa + snap gợi ý
    if (isDragDoorRef.current) {
      updateMouseFromEvent(event);

      const scene = sceneRef.current;
      const currentCabinet = sceneManager.cabinetManager.getCurrentCabinet();

      // di chuyển theo plane
      if (scene.plane) {
        raycasterRef.current.ray.intersectPlane(
          scene.plane,
          scene.planeIntersect
        );
      }
      if (scene.dragObject && scene.planeIntersect && scene.shift) {
        //Tạm thời chặn kh cho kéo cửa xuống quá sàn
        if (scene.dragObject.position.y < 0) {
          scene.dragObject.position.y = 0;
          return;
        }

        scene.dragObject.position.addVectors(scene.planeIntersect, scene.shift);
      }

      insideLoaderRef?.current?.movePointDoor?.(
        scene.dragObject?.userData?.index,
        scene.dragObject,
        scene.arrayPointDoor,
        meshClickRef.current
      );
      insideLoaderRef?.current?.scaleDoorAdvanced?.(
        scene.dragObject?.userData?.index,
        meshClickRef.current,
        scene.arrayPointDoor
      );

      const hit = raycasterRef.current.intersectObjects(
        scene.arrayPointDoor,
        true
      );
      if (hit?.length) {
        const point = hit[0].point;
        if (
          scene.dragObject?.userData?.index === 0 ||
          scene.dragObject?.userData?.index === 1
        ) {
          point.y += 0.03;
        } else {
          point.y -= 0.03;
        }

        const result = insideLoaderRef?.current?.getPointDoorRecomment?.(
          point,
          currentCabinet,
          currentCabinet.cloneModule,
          meshClickRef.current
        );

        const hintPoints = scene?.drawPointRecommendDoor?.(
          result,
          0,
          // Number(currentCabinet?.doorType) ?? 0
          doorCornersRef.current,
          meshClickRef.current
        );

        const snapP = point;
        if (
          scene.dragObject?.userData?.index === 0 ||
          scene.dragObject?.userData?.index === 1
        ) {
          snapP.y -= 0.03;
        } else {
          snapP.y += 0.03;
        }

        const closest = findClosestPoint(
          meshClickRef.current,
          snapP,
          hintPoints,
          SnapDoor.MIN_DISTANCE
        );
        if (closest) {
          closest.scale?.set?.(1.5, 1.5, 1.5);
          closest.material?.color?.set?.(0x16ff00);
        }
        pointSnapRef.current = closest || null;
      }
    }
  };

  const handleMouseUpExtended = () => {
    if (!sceneRef.current) return;

    // 1) Thả preview → commit real model
    if (isDraggingRef.current && previewModelRef.current) {
      isDraggingRef.current = false;

      const hits = raycasterRef.current.intersectObjects(
        sceneRef.current.planToCheck.children,
        true
      );
      const drop = hits[0]?.point;

      sceneRef.current.scene.remove(previewModelRef.current);

      const currentCabinet = sceneManager.cabinetManager.getCurrentCabinet();
      const cloneModule =
        sceneManager.cabinetManager.currentCabinet.getCloneModule();
      if (currentCabinet && cloneModule) {
        loadRealModelWithInsideLoader(
          draggedModuleRef.current,
          drop,
          cloneModule,
          currentCabinet
        );
      }

      previewModelRef.current = null;
      draggedModuleRef.current = null;
    }

    // 2) Thả cửa
    if (isDragDoorRef.current) {
      const scene = sceneRef.current;
      const hits = raycasterRef.current.intersectObjects(
        scene.planToCheck.children,
        true
      );
      const drop = hits[0]?.point;

      if (pointSnapRef.current) {
        //Cập nhật lại độ cao để set drop chính xác
        drop.y = pointSnapRef.current.position.y;

        const pointPos = pointSnapRef.current.getWorldPosition(
          new THREE.Vector3()
        );
        const meshPos = meshClickRef.current.getWorldPosition(
          new THREE.Vector3()
        );

        const dx = roundTo(Math.abs(pointPos.x - meshPos.x));
        const dz = roundTo(Math.abs(pointPos.z - meshPos.z));

        const currentCabinet = sceneManager.cabinetManager.getCurrentCabinet();

        if (
          meshClickRef.current.userData?.drawerUuid ||
          (!meshClickRef.current.userData?.drawerUuid &&
            Math.max(dx, dz) <= LimitDoor.WIDTH)
        ) {
          insideLoaderRef?.current?.snapToPointDoorRecommend?.(
            scene.dragObject?.userData?.index,
            pointSnapRef.current,
            scene.arrayPointDoor,
            meshClickRef.current
          );

          insideLoaderRef?.current?.scaleDoorAdvanced?.(
            scene.dragObject?.userData?.index,
            meshClickRef.current,
            scene.arrayPointDoor,
            drop,
            currentCabinet,
            currentCabinet?.cloneModule
          );

          //Tạm thời phân biệt cửa cánh kính qua tên
          const materialType = meshClickRef?.current?.userData?.materialType;

          if (!materialType?.includes('tron')) {
            insideLoaderRef?.current.cutSpecialDoor(
              currentCabinet.cloneModule,
              meshClickRef.current,
              materialType
            );
          }
        } else {
          //Scale cửa về giá trị cũ trước đó khi KHÔNG THOẢ MÃN KÍCH THƯƠC TỐI ĐA
          insideLoaderRef?.current?.scaleDoorAdvanced?.(
            scene.dragObject?.userData?.index,
            meshClickRef.current,
            scene.arrayPointDoorPrev,
            drop,
            currentCabinet,
            currentCabinet?.cloneModule
          );
        }
      } else {
        //Scale cửa về giá trị cũ trước đó khi KHÔNG CÓ SNAP
        const currentCabinet = sceneManager.cabinetManager.getCurrentCabinet();

        insideLoaderRef?.current?.scaleDoorAdvanced?.(
          scene.dragObject?.userData?.index,
          meshClickRef.current,
          scene.arrayPointDoorPrev,
          drop,
          currentCabinet,
          currentCabinet?.cloneModule
        );
      }

      isDragDoorRef.current = false;
      scene.dragObject = null;
      if (scene.controls) {
        scene.controls.enabled = true;
      }
      scene?.removePointRecommend?.();
      pointSnapRef.current = null;

      // HIỂN THỊ LẠI BUTTON NẾU CÒN MESH
      if (meshClickRef.current) {
        updateMoveButtonPosition(meshClickRef.current);
      }

      setTick(value => value + 1);
    }

    // 3) Thả holder
    if (isDragHolderRef.current) {
      isDragHolderRef.current = false;
      if (sceneRef.current.controls) {
        sceneRef.current.controls.enabled = true;
      }

      // HIỂN THỊ LẠI BUTTON NẾU CÒN MESH
      if (meshClickRef.current) {
        updateMoveButtonPosition(meshClickRef.current);
      }
    }

    // 4) Thả inside
    if (isDragInsideRef.current) {
      isDragInsideRef.current = false;
      if (sceneRef.current.controls) {
        sceneRef.current.controls.enabled = true;
      }
      sceneRef.current?.display?.removeBoxes?.();
      sceneRef.current?.display?.removePoints?.();
      sceneRef.current?.display?.removeWireframeBoxes?.();

      // HIỂN THỊ LẠI BUTTON NẾU CÒN MESH
      if (meshClickRef.current) {
        updateMoveButtonPosition(meshClickRef.current);
      }

      isDragInside2Ref.current = false;
      setTick(value => value + 1);
    }
  };

  // ========= DELETE: xoá inside/door/holder =========
  const handleDeleteKeyPress = useCallback(() => {
    if (!meshClickRef.current || !sceneRef.current) return;

    const activeElement = document.activeElement;
    const isTyping =
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.isContentEditable;

    if (isTyping) {
      return; // Không làm gì, để người dùng xóa text bình thường
    }

    const currentCabinet = sceneManager.cabinetManager.getCurrentCabinet();
    const cloneModule =
      sceneManager.cabinetManager.currentCabinet.getCloneModule();
    const cbGroup = cloneModule?.group;
    if (!cbGroup) return;

    const picked = meshClickRef.current;

    insideLoaderRef?.current?.removeInside(picked, cloneModule);

    meshClickRef.current = null;
    setMdTypeClicked(null);
    childClickRef.current = null;

    currentCabinet?.findPoint?.();
    sceneRef.current?.display?.removeBoxes?.();
    sceneRef.current?.removeAllPoints?.();
    sceneRef.current?.display?.removeWireframeBoxes?.();
    sceneRef.current?.removeBoxes?.();
    sceneRef.current?.removeAllPoints?.();
    sceneRef.current?.removeWireframeBoxes?.();
    insideLoaderRef.current?.clearLine();
    insideLoaderRef.current?.clearLabel();
    setShowMoveButton(false); // ẨN BUTTON
    sceneRef.current?.drawEdgeLine?.();
    setTick(value => value + 1);
  }, [
    meshClickRef.current,
    sceneRef.current,
    insideLoaderRef.current,
    sceneManager,
    childClickRef.current,
  ]);

  const handleOtherKeyPress = useCallback(
    e => {
      //Chỉ áp dụng trên trang editor
      if (!open) return;

      if (e.altKey && e.code === 'KeyD') {
        // xử lý dim kích thước ở đây
        setIsDimensioning(prev => {
          if (sceneRef.current)
            if (prev) {
              sceneRef.current?.clearAllCornerPoints?.();
            } else {
              sceneRef.current?.generateCornerPoints?.();
            }
          return !prev;
        });
      }
      if (e.altKey && e.code === 'KeyQ') {
        // xử lý tính khoảng cách ở đây
        sceneRef.current?.positionController?.createDistanceLines?.();
      }
      if (e.code === 'KeyH') {
        // xử lý ẩn hiện cửa ở đây
        setIsHideDoor(prev => {
          if (sceneRef.current) {
            // ShortcutUtils.visibleDoor(prev, isOpenDoor, sceneRef.current);
            ShortcutUtils.visibleDoor(prev, false, sceneRef.current);

            return !prev;
          }
        });
      }
    },
    [sceneRef.current, open]
  );

  useEffect(() => {
    const onKey = e => {
      // Ignore input fields
      const activeElement = document.activeElement;
      const isTyping =
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable;

      if (isTyping) {
        return; // Không làm gì, để người dùng xóa text bình thường
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        handleDeleteKeyPress(e);
      } else {
        handleOtherKeyPress(e);
      }
    };

    document.addEventListener('keydown', onKey);

    return () => document.removeEventListener('keydown', onKey);
  }, [handleOtherKeyPress, handleDeleteKeyPress]);

  // Thêm useEffect để update button khi camera thay đổi
  useEffect(() => {
    if (!open || !meshClickRef.current || !showMoveButton) return;

    const updateButtonOnRender = () => {
      if (meshClickRef.current && showMoveButton) {
        updateMoveButtonPosition(meshClickRef.current);
      }
    };

    // Gọi update mỗi frame
    const intervalId = setInterval(updateButtonOnRender, 16); // ~60fps

    return () => clearInterval(intervalId);
  }, [open, showMoveButton]);

  // ========= Wire global listeners khi open =========
  useEffect(() => {
    if (!open) return;

    const onMove = e => {
      if (
        isDraggingRef.current &&
        previewModelRef.current &&
        sceneRef.current
      ) {
        const canvas = document.getElementById('canvas-cabinet');
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        mousePositionRef.current.x =
          ((e.clientX - rect.left) / rect.width) * 2 - 1;
        mousePositionRef.current.y =
          -((e.clientY - rect.top) / rect.height) * 2 + 1;

        raycasterRef.current.setFromCamera(
          mousePositionRef.current,
          sceneRef.current.camera
        );

        const hits = raycasterRef.current.intersectObjects(
          sceneRef.current.planToCheck.children,
          true
        );

        if (hits.length) {
          const intersectionPoint = hits[0].point;

          const currentCabinet =
            sceneManager?.cabinetManager?.getCurrentCabinet();
          const clonedModule =
            sceneManager?.cabinetManager?.currentCabinet?.getCloneModule();

          if (!currentCabinet || !clonedModule) {
            previewModelRef.current.position.copy(
              getAdjustedPoint(intersectionPoint, previewModelRef.current)
            );
            hideSnapLine();
            return;
          }

          // LẤY TYPE TỪ draggedModuleRef
          const previewType = draggedModuleRef.current?.type;

          // TYPE 1: Snap dọc (theo Z)
          if (previewType === 1) {
            const edges = findClosestZEdges(
              intersectionPoint,
              currentCabinet,
              clonedModule
            );

            if (edges && edges.leftEdge && edges.rightEdge) {
              const centerPoint = calculateCenterPointVertical(
                edges.leftEdge,
                edges.rightEdge,
                clonedModule,
                intersectionPoint
              );

              if (centerPoint) {
                centerPointRef.current = centerPoint;
                const isNear = isNearCenterPoint(
                  intersectionPoint,
                  centerPoint,
                  previewType
                );

                // Hiển thị snap line dọc
                createOrUpdateSnapLine(
                  centerPoint,
                  clonedModule,
                  previewType,
                  isNear
                );

                if (isNear) {
                  const snappedPoint = getAdjustedPoint(
                    centerPoint.clone(),
                    previewModelRef.current
                  );
                  previewModelRef.current.position.copy(snappedPoint);
                } else {
                  const adjustedPoint = getAdjustedPoint(
                    intersectionPoint,
                    previewModelRef.current
                  );
                  previewModelRef.current.position.copy(adjustedPoint);
                }

                handleMouseMoveExtended(e);
                return;
              }
            }
          }

          // TYPE 0: Snap ngang (theo Y)
          else if (previewType === 0) {
            const edges = findClosestYEdges(
              intersectionPoint,
              currentCabinet,
              clonedModule
            );

            if (edges && edges.bottomEdge && edges.topEdge) {
              const centerPoint = calculateCenterPointHorizontal(
                edges.bottomEdge,
                edges.topEdge,
                clonedModule,
                intersectionPoint
              );

              if (centerPoint) {
                centerPointRef.current = centerPoint;
                const isNear = isNearCenterPoint(
                  intersectionPoint,
                  centerPoint,
                  previewType
                );

                // Hiển thị snap line ngang
                createOrUpdateSnapLine(
                  centerPoint,
                  clonedModule,
                  previewType,
                  isNear
                );

                if (isNear) {
                  const snappedPoint = getAdjustedPoint(
                    centerPoint.clone(),
                    previewModelRef.current
                  );
                  previewModelRef.current.position.copy(snappedPoint);
                } else {
                  const adjustedPoint = getAdjustedPoint(
                    intersectionPoint,
                    previewModelRef.current
                  );
                  previewModelRef.current.position.copy(adjustedPoint);
                }

                handleMouseMoveExtended(e);
                return;
              }
            }
          }

          // Fallback
          previewModelRef.current.position.copy(
            getAdjustedPoint(intersectionPoint, previewModelRef.current)
          );
          hideSnapLine();
        } else {
          hideSnapLine();
        }
      }

      handleMouseMoveExtended(e);
    };

    const onUp = () => {
      if (
        isDraggingRef.current &&
        previewModelRef.current &&
        sceneRef.current
      ) {
        isDraggingRef.current = false;
        const hits = raycasterRef.current.intersectObjects(
          sceneRef.current.planToCheck.children,
          true
        );

        const previewType = draggedModuleRef.current?.type;

        const dropPoint = (() => {
          const originalDrop = hits[0]?.point;
          if (!originalDrop) return originalDrop;

          if (
            centerPointRef.current &&
            isNearCenterPoint(originalDrop, centerPointRef.current, previewType)
          ) {
            console.log(
              `Snapped to center point! (Type: ${
                previewType === 1 ? 'Dọc' : 'Ngang'
              })`
            );
            return centerPointRef.current.clone();
          }

          return originalDrop;
        })();

        sceneRef.current.scene.remove(previewModelRef.current);

        const currentCabinet = sceneManager.cabinetManager.getCurrentCabinet();
        const clonedModule =
          sceneManager.cabinetManager.currentCabinet.getCloneModule();

        const hauMeshes = [];
        clonedModule.group.traverse(child => {
          if (child.isMesh && child.name.includes('HAU')) {
            hauMeshes.push(child);
          }
        });

        // Raycaster trực tiếp trên array mesh (không cần group)
        const hits1 = raycasterRef.current.intersectObjects(hauMeshes, true);
        const originalMeshName = hits1[0]?.object.name;

        let typeHau;
        if (
          originalMeshName &&
          clonedModule.cabinetType.toLowerCase().includes('corner')
        ) {
          if (originalMeshName.includes('HAU-1')) {
            typeHau = TypeConnerModule.SUB;
          } else if (originalMeshName.includes('HAU-2')) {
            typeHau = TypeConnerModule.MAIN;
          }
        }

        if (currentCabinet && clonedModule && dropPoint) {
          loadRealModelWithInsideLoader(
            draggedModuleRef.current,
            dropPoint,
            clonedModule,
            currentCabinet,
            typeHau
          );
        }

        previewModelRef.current = null;
        draggedModuleRef.current = null;
        hideSnapLine();
      }

      handleMouseUpExtended();
    };

    const onDown = e => handleMouseDown(e);
    const onClick = e => handleCanvasClick(e);

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('click', onClick);

    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('click', onClick);
    };
  }, [open, sceneManager]);

  useEffect(() => {
    if (!open) return;
    const cloneModule =
      sceneManager.cabinetManager.currentCabinet.getCloneModule();

    // //Auto thêm cửa cho hộc kéo
    // if (sidebarKey === "1" || sidebarKey === "4") {
    //   if (cloneModule) {
    //     insideLoaderRef.current.scaleDoorForDrawer(cloneModule);
    //   }
    // }
    if (!cloneModule) return;

    cloneModule.group.traverse(object => {
      //Ẩn hiện cửa và tay nắm khi chuyển tab trong editor
      if (
        object.userData.type === InsideType.DOOR ||
        object.userData.type === InsideType.HANDLE
      ) {
        if (sidebarKey === '1' || sidebarKey === '4') {
          object.visible = true;
          object.raycast = THREE.Mesh.prototype.raycast;
        } else {
          object.visible = false;
          object.raycast = () => {};
        }
      }
    });

    setTick(value => value + 1);
  }, [sidebarKey, open]);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => {
      sceneRef.current.handleDrawBorder();
    }, 50);
  }, [tick, open]);

  return (
    <CabinetEditorContext.Provider value={contextValue}>
      <BottomSheet
        onClose={onClose}
        classNames={{
          title: 'flex justify-between self-stretch items-center px-24 py-12',
          body: `cabinet-content ${cabinetType === 'kitchen' ? '!p-0' : ''}`,
        }}
        title={
          <>
            <span className="text-headline-small">
              {t('Advanced Customization')}
            </span>
            <div className="flex items-center gap-24">
              {canSaveToLibrary(user) && (
                <ButtonSecondaryLarge
                  posIcon={<Icon.ICSave />}
                  title={t('Save Module to Library')}
                  onClick={handleOpenSaveLibraryModal} // Mở modal thay vì lưu trực tiếp
                  loading={false}
                  disabled={false}
                />
              )}

              <ButtonPrimaryLarge
                title={t('Save Changes')}
                preIcon={<Icon.ICArrowForward />}
                onClick={handleConfirm}
                loading={false}
                disabled={false}
              />
              <ButtonIconGhostLarge icon={<Icon.ICClose />} onClick={onClose} />
            </div>
          </>
        }
        open={open}
      >
        {open && (
          <>
            <div id="container__canvas-cabinet" className="flex items-center">
              <canvas id="canvas-cabinet" />
            </div>

            {showMoveButton && moveButtonPos && (
              <button
                className="btn-inside-move"
                style={{
                  position: 'fixed',
                  top: moveButtonPos.y,
                  left: moveButtonPos.x,
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1000,
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  cursor: 'move',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span className="w-[16px] h-[16px] flex">
                  <Icon.ICDragPan />
                </span>
              </button>
            )}

            {
              <>
                <LeftCabinetDesignSidebar
                  activeKey={sidebarKey}
                  setActiveKey={setSidebarKey}
                />
                <div className="cabinet-editor-container-toolbar">
                  <LeftCabinetCustomDesignMenu
                    open={sidebarKey === '1' && mdCustomData.type !== 1}
                    form={formLeftCabinetCustom}
                  />
                  <LeftColumnCabinetCustomDesignMenu
                    open={sidebarKey === '1' && mdCustomData.type === 1}
                    form={formLeftCabinetCustom}
                  />
                  <LeftInsideDesignMenu
                    open={sidebarKey === '2'}
                    onDragStart={handleDragStart} // kéo inside
                    title={t('Functional department')}
                    productType={productType}
                  />
                  <LeftAccessoryCustomDesignMenu
                    open={sidebarKey === '3'}
                    onDragStart={handleDragStart}
                    title={t('Accessories')}
                    productType={productType}
                    sceneManager={sceneManager}
                  />
                  <LeftDoorCustomDesignMenu
                    open={sidebarKey === '4'}
                    onDragStart={handleDragStart} // kéo cửa
                    productType={productType}
                    groupDoorId={groupDoorId}
                    setGroupDoorId={setGroupDoorId}
                    beveledDoor={beveledDoor}
                    setBeveledDoor={setBeveledDoor}
                  />
                  <LeftLightingCustomDesignMenu
                    open={sidebarKey === '5'}
                    onDragStart={handleDragStart}
                    title={t('Lighting')}
                    productType={productType}
                    sceneManager={sceneManager}
                  />
                  <div
                    className={
                      'cabinet-editor-container-toolbar-extra relative'
                    }
                    style={{ left: sidebarKey ? '366px' : '86px' }}
                  >
                    <ToolbarDesign
                      sceneManager={sceneRef?.current}
                      isDimensioning={isDimensioning}
                      setIsDimensioning={setIsDimensioning}
                      isHideDoor={isHideDoor}
                      setIsHideDoor={setInsideType}
                    />
                  </div>
                </div>
                <RightDoorCustomDesignMenu
                  open={
                    sidebarKey === '4' &&
                    meshClickRef.current?.userData?.type === InsideType.DOOR
                  }
                  form={formLeftCabinetCustom}
                  model={meshClickRef.current}
                  onClickCard={handleChooseDoorAccessory}
                  beveledDoor={beveledDoor}
                  setBeveledDoor={setBeveledDoor}
                />
                <RightInsideCustomDesignMenu
                  open={sidebarKey === '2' && meshClickRef.current}
                  // open={sidebarKey === "2" && mdTypeClicked}
                  form={formLeftCabinetCustom}
                />
              </>
            }
          </>
        )}
      </BottomSheet>
      <SaveModuleLibraryModal
        open={showSaveLibraryModal}
        onClose={() => setShowSaveLibraryModal(false)}
        onSave={handleSaveToLibrary}
      />
      <LoadingOverlay visible={loading} text={t('Saving module')} />
    </CabinetEditorContext.Provider>
  );
};

export default CabinetEditor;
